import _ from "lodash";
import { useEffect } from "react";
import { useLoaderData } from "react-router-dom";

import { inject } from "../../../../../../lib/itn-ui-kit/services/service-container";
import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import { getDeepDiff, updateAddress } from "../helper/functions";
import { useViewDetailStore } from "../store/viewDetailStore";

import useFetchData from "./useFetchData";
import useViewNavigationService from "./useViewNavigationService";

export default function useOnClickEventListener() {
  const { viewName, id } = useLoaderData();
  const addressRepository = useService("repositories/address-repository");
  const { ensureCanAccessView, navigateToNotFound } = useViewNavigationService();

  const { openSuccessModal, openFailureModal } = getModalHandler();
  const {
    initialViewDetail,
    modifiedViewDetail,
    setModifiedViewDetail,
    setOpenModal,
    setEnableSaveBtn,
    setEnableCancelBtn,
  } = useViewDetailStore();
  const { fetchViewDetails } = useFetchData();

  useEffect(() => {
    if (initialViewDetail && modifiedViewDetail) {
      const isModified = !_.isEqual(initialViewDetail, modifiedViewDetail);
      setEnableSaveBtn(isModified);
      setEnableCancelBtn(isModified);
    }
  }, [initialViewDetail, modifiedViewDetail, setEnableSaveBtn, setEnableCancelBtn]);

  const handleOnChangeFieldValue = (fieldName, fieldValue) => {
    const updated = _.cloneDeep(modifiedViewDetail ?? {});
    _.set(updated, fieldName, fieldValue);
    setModifiedViewDetail(updated);
  };

  const handleOnClickCancelButton = () => {
    setOpenModal(false);
    setModifiedViewDetail(initialViewDetail);
  };

  const handleOnClickEditButton = () => {
    setOpenModal(true);
  };

  const handleOnClickCloseModalButton = () => {
    handleOnClickCancelButton();
  };

  const handleOnClickSaveButton = async () => {
    if (!ensureCanAccessView(viewName, true)) {
      return;
    }

    const repositoryKey = `repositories/${viewName}-repository`;
    let viewRepository;

    try {
      viewRepository = inject(repositoryKey);
    } catch (error) {
      navigateToNotFound(true);
      return;
    }

    try {
      const diff = getDeepDiff(modifiedViewDetail, initialViewDetail);

      if (!diff) {
        return;
      }
      const payload = { ...diff };

      if (diff.address) {
        await updateAddress(modifiedViewDetail.address, addressRepository);
        payload.address = modifiedViewDetail.address;
      }

      if (Object.keys(payload).length) {
        await viewRepository.patch(id, payload);
      }

      setOpenModal(false);
      openSuccessModal();
      fetchViewDetails();
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    }
  };

  return {
    handleOnClickEditButton,
    handleOnClickCancelButton,
    handleOnClickCloseModalButton,
    handleOnChangeFieldValue,
    handleOnClickSaveButton,
  };
}
